import { useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Loader2, Settings } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

import Resizer from "react-image-file-resizer";

const resizeFile = (file: File, maxWidth: number, maxHeight: number, format: string, quality: number) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      format,
      quality,
      0,
      (uri) => {
        resolve(uri);
      },
      "blob"
    );
  });

interface FileUploadProps {
  value?: string;
  onChange: (url: string) => void;
  accept?: string;
  placeholder?: string;
  maxWidth?: number;
  maxHeight?: number;
  compressFormat?: "JPEG" | "PNG" | "WEBP";
  quality?: number;
}

export function FileUpload({ 
    value, 
    onChange, 
    accept = "image/*", 
    placeholder="Upload file",
    maxWidth = 1200,
    maxHeight = 1200,
    compressFormat = "JPEG",
    quality = 80
}: FileUploadProps) {
    const [uploading, setUploading] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [localFormat, setLocalFormat] = useState<"JPEG" | "PNG" | "WEBP">(compressFormat);
    const [localQuality, setLocalQuality] = useState(quality);
    const [localWidth, setLocalWidth] = useState(maxWidth);
    const [localHeight, setLocalHeight] = useState(maxHeight);
    const { toast } = useToast();
  
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
  
      setUploading(true);
      
      let fileToUpload = file;
  
      // Resize if image
      if (file.type.startsWith("image/")) {
          try {
              const resizedImage = await resizeFile(file, localWidth, localHeight, localFormat, localQuality);
               // Create a new File object from Blob to keep name
               const extension = localFormat === "JPEG" ? ".jpg" : localFormat === "PNG" ? ".png" : ".webp";
               const mimeType = localFormat === "JPEG" ? "image/jpeg" : localFormat === "PNG" ? "image/png" : "image/webp";
               
               fileToUpload = new File(
                   [resizedImage as Blob], 
                   file.name.replace(/\.[^/.]+$/, extension), 
                   { type: mimeType }
               );
          } catch (err) {
              console.error("Resize failed", err);
              // Fallback to original
          }
      }
  
      const formData = new FormData();
      formData.append("file", fileToUpload);
  
      try {
        const res = await fetch(`${BACKEND_URL}/upload.php`, {
          method: "POST",
          body: formData,
        });
        const data = await res.json();
        
        if (data.status === "success") {
          onChange(data.url);
          toast({ title: "Success", description: "File uploaded successfully" });
        } else {
          throw new Error(data.message);
        }
      } catch (err: any) {
        console.error(err);
        toast({ variant: "destructive", title: "Upload Failed", description: err.message });
      } finally {
        setUploading(false);
        // Reset input
        e.target.value = "";
      }
    };
  
    return (
      <div className="space-y-3">
        <div className="flex gap-2 items-center">
          <Input 
            value={value} 
            onChange={(e) => onChange(e.target.value)} 
            placeholder={placeholder || "https://..."} 
            className="flex-1"
          />
           <div className="relative">
              <input
                type="file"
                accept={accept}
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
              />
               <Button type="button" variant="outline" size="icon" disabled={uploading}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              </Button>
           </div>
           
           {accept.startsWith("image/") && (
               <Button 
                type="button" 
                variant={showSettings ? "default" : "outline"} 
                size="icon" 
                onClick={() => setShowSettings(!showSettings)}
                title="Compression Settings"
               >
                   <Settings className="w-4 h-4" />
               </Button>
           )}
  
           {value && (
              <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => onChange("")}
                  className="text-muted-foreground hover:text-destructive"
              >
                  <X className="w-4 h-4" />
              </Button>
           )}
        </div>
        
        {showSettings && accept.startsWith("image/") && (
            <div className="p-4 border rounded-md bg-muted/50 space-y-4 text-sm animate-in slide-in-from-top-2">
                <div className="flex items-center justify-between">
                    <h4 className="font-medium text-foreground">Image Optimization</h4>
                    <Button variant="ghost" size="sm" onClick={() => setShowSettings(false)} className="h-6 w-6 p-0"><X className="w-3 h-3" /></Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Format</label>
                        <select 
                            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                            value={localFormat}
                            onChange={(e) => setLocalFormat(e.target.value as any)}
                        >
                            <option value="JPEG">JPEG (Smallest)</option>
                            <option value="WEBP">WEBP (Modern)</option>
                            <option value="PNG">PNG (Lossless)</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Quality ({localQuality}%)</label>
                        <input 
                            type="range" 
                            min="10" 
                            max="100" 
                            value={localQuality}
                            onChange={(e) => setLocalQuality(Number(e.target.value))}
                            className="w-full h-9 cursor-pointer"
                        />
                    </div>
                </div>
  
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Max Width (px)</label>
                        <Input 
                            type="number" 
                            value={localWidth} 
                            onChange={(e) => setLocalWidth(Number(e.target.value))} 
                            className="h-8"
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Max Height (px)</label>
                        <Input 
                            type="number" 
                            value={localHeight} 
                            onChange={(e) => setLocalHeight(Number(e.target.value))} 
                            className="h-8"
                        />
                    </div>
                </div>
                <p className="text-[10px] text-muted-foreground">Note: Images will be resized to fit within these dimensions while maintaining aspect ratio.</p>
            </div>
        )}
  
        {value && accept.startsWith("image/") && (
          <div className="relative w-24 h-24 rounded-md overflow-hidden border">
              <img src={`https://baelleaves.com/${value}`} alt="Preview" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
         {value && accept.startsWith("video/") && (
          <div className="relative w-32 h-24 rounded-md overflow-hidden border bg-black">
              <video src={value} className="w-full h-full object-contain" controls preload="none" />
          </div>
        )}
      </div>
    );
  }
